// General staff entry. Behavior:
//   • signed out                   → render the inline staff sign-in form
//   • signed in, 0 stations        → redirect to /access-denied
//   • signed in, 1 station         → redirect to /<slug>/admin
//   • signed in, >1 stations       → Landing scope (below)
//
// CTV-759 / ADR-0036 Landing scope: a manager or organization_admin with
// more than one station lands on /admin/all, because a comprehensive view is
// the POINT of the surface rather than an option inside it. A super_admin
// lands on the picker, because their administrable set spans every
// organization and a cross-org revenue blend is a platform rollup wearing a
// publisher view's clothes — they reach All stations from its own card. The
// picker renders a search box once the list exceeds 10 entries.

import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  Loader2,
  HelpCircle,
  LayoutGrid,
  Upload,
  Clapperboard,
} from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import StaffSignInForm from '../../components/self-service/auth/StaffSignInForm';
import CtvBuyerAuthShell, { CTV_NAVY } from '../../components/self-service/auth/CtvBuyerAuthShell';
import { useAdminHelpUnread } from '../../components/self-service/help/useAdminHelpUnread';
import { useAdminPublicaUploadsCount } from '../../components/self-service/admin/useAdminPublicaUploadsCount';

const SEARCH_THRESHOLD = 10;

const AdminEntryPage: React.FC = () => {
  const { isAuthenticated, user, myAdminStations } = useAuth();
  const isSuperAdmin = user?.rawRole === 'super_admin';
  const { count: adminHelpUnread } = useAdminHelpUnread(isSuperAdmin);
  const { count: publicaUploadsCount } = useAdminPublicaUploadsCount(isSuperAdmin);
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = React.useState('');

  // After auth, honor a deep-link target carried in router state by
  // PublisherAdminGate / PublisherOnboardPage. Restrict to admin paths to
  // avoid open-redirect surprises. Falls through to the solo-station
  // auto-pick when no valid `from` is present.
  React.useEffect(() => {
    if (!isAuthenticated || myAdminStations == null) return;
    const from = (location.state as { from?: string } | null)?.from;
    if (typeof from === 'string' && from.startsWith('/admin/') && from !== '/admin') {
      navigate(from, { replace: true });
      return;
    }
    if (myAdminStations.length === 1 && myAdminStations[0].slug) {
      navigate(`/admin/${myAdminStations[0].slug}`, { replace: true });
      return;
    }
    // Landing scope. Deliberately AFTER the `from` branch: that branch is
    // what carries a manager's deep link through PublisherAdminGate's
    // sign-in round trip, and it must keep winning.
    if (myAdminStations.length > 1 && !isSuperAdmin) {
      navigate('/admin/all', { replace: true });
    }
  }, [isAuthenticated, myAdminStations, navigate, location.state, isSuperAdmin]);

  if (!isAuthenticated || !user) {
    return (
      <CtvBuyerAuthShell variant="dark" eyebrow="Streamline Publisher Portal">
        <div className="bg-white rounded-[20px] shadow-2xl p-8 w-full">
          <div className="text-center mb-6">
            <h1 className="text-xl font-semibold" style={{ color: CTV_NAVY }}>
              Publisher Login
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Sign in to your internal Streamline page
            </p>
          </div>
          <StaffSignInForm />
        </div>
      </CtvBuyerAuthShell>
    );
  }

  if (myAdminStations == null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your stations…
      </div>
    );
  }

  if (myAdminStations.length === 0) {
    return <Navigate to="/access-denied" replace />;
  }

  // Solo-redirect and the multi-station Landing scope redirect both run in
  // the effect above; render a brief placeholder so we don't flash the
  // picker for either case.
  if (myAdminStations.length === 1 || (myAdminStations.length > 1 && !isSuperAdmin)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Redirecting…
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const visible = q
    ? myAdminStations.filter((s) =>
        [s.name, s.displayName ?? '', s.slug ?? '']
          .map((v) => v.toLowerCase())
          .some((v) => v.includes(q)),
      )
    : myAdminStations;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* CTV-732: creatives waiting to be placed in a Publica creative
            library. Mirrors the Help Center card's shape; the count badge is
            the same standing worklist the sidebar badge shows. */}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin/publica-uploads')}
            className="w-full flex items-center justify-between px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-3">
              <Upload className="w-5 h-5 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Publica uploads</div>
                <div className="text-xs text-slate-500">
                  Creatives waiting to be added to a Publica creative library
                </div>
              </div>
            </div>
            {publicaUploadsCount > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-xs font-bold text-white flex items-center justify-center">
                {publicaUploadsCount > 9 ? '9+' : publicaUploadsCount}
              </span>
            )}
          </button>
        )}
        {/* CTV-759: super_admins land here rather than on All stations, so
            the scope needs a door. Same card shape as the two above. */}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin/all')}
            className="w-full flex items-center justify-between px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-3">
              <LayoutGrid className="w-5 h-5 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">All stations</div>
                <div className="text-xs text-slate-500">
                  Combined overview, submissions, and advertisers across every station you administer
                </div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </button>
        )}
        {/* CTV-800: the staff Waymark studio. Same card shape as its siblings;
            this is the only door to it, since it has no nav item in the
            legacy dashboard. */}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin/studio')}
            className="w-full flex items-center justify-between px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-3">
              <Clapperboard className="w-5 h-5 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Studio</div>
                <div className="text-xs text-slate-500">
                  Make and download Waymark commercials for direct clients
                </div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </button>
        )}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin/help-center')}
            className="w-full flex items-center justify-between px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Help Center</div>
                <div className="text-xs text-slate-500">Support tickets from all stations</div>
              </div>
            </div>
            {adminHelpUnread > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-xs font-bold text-white flex items-center justify-center">
                {adminHelpUnread > 9 ? '9+' : adminHelpUnread}
              </span>
            )}
          </button>
        )}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-200">
          <h1 className="text-xl font-semibold text-slate-900">Choose a station</h1>
          <p className="text-sm text-slate-600 mt-1">
            You have admin access to {myAdminStations.length} stations. Pick one to
            open its admin tools — you can change station any time from the
            selector beside each page heading.
          </p>
        </div>

        {myAdminStations.length > SEARCH_THRESHOLD && (
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stations"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {visible.length === 0 ? (
            <li className="px-6 py-8 text-center text-sm text-slate-500">
              No stations match "{search}".
            </li>
          ) : (
            visible.map((s) => (
              <li key={s.slug ?? s.name}>
                <button
                  type="button"
                  disabled={!s.slug}
                  onClick={() => s.slug && navigate(`/admin/${s.slug}`)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-left transition-colors"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {s.displayName || s.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {s.slug ? `/admin/${s.slug}` : 'Slug not set'}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
      </div>
    </div>
  );
};

export default AdminEntryPage;
